<template>
  <div class="user-emails-selector" data-testid="user-emails-selector">
    <slot name="label">
      <label v-if="label" class="form-label" :class="{ 'is-required': required }">
        {{ label }}
        <span v-if="required" class="required-indicator">*</span>
      </label>
    </slot>
    <div class="d-flex flex-wrap align-items-center gap-2">
      <span
        v-for="contact in selectedContacts"
        :key="contact.id ? `id-${contact.id}` : `email-${contact.email.toLowerCase()}`"
        :data-testid="`user-emails-selector-chip-${encodeURIComponent(contact.email.toLowerCase())}`"
        class="email-chip badge bg-light text-dark d-inline-flex align-items-center gap-1 py-1 px-2">
        <span class="chip-label">{{ contact.email }}</span>
        <IconX
          v-if="!disabled"
          :data-testid="`user-emails-selector-remove-${encodeURIComponent(contact.email.toLowerCase())}`"
          :size="14"
          class="chip-remove cursor-pointer text-muted"
          @click="removeContact(contact.email)" />
      </span>

      <div v-if="!disabled" class="selector-popover" ref="popoverRef">
        <button
          type="button"
          class="btn btn-icon rounded-circle selector-popover-trigger"
          data-testid="user-emails-selector-open"
          @click.prevent="togglePopover">
          <IconPlus :size="16" />
        </button>

        <div v-if="isPopoverOpen" class="selector-popover-menu card shadow-sm">
          <div v-if="step === 'pick-type'" class="py-1 selector-popover-type-list">
            <a
              href="#"
              class="selector-popover-item d-flex align-items-center gap-2 py-2 px-3"
              data-testid="user-emails-selector-mode-existing"
              @click.prevent="selectMode('existing')">
              <IconUser :size="16" /> {{ $t('user_emails_selector.existing_user') }}
            </a>
            <a
              href="#"
              class="selector-popover-item d-flex align-items-center gap-2 py-2 px-3"
              data-testid="user-emails-selector-mode-manual"
              @click.prevent="selectMode('manual')">
              <IconMail :size="16" /> {{ $t('user_emails_selector.insert_email') }}
            </a>
          </div>

          <div v-else class="p-3 selector-popover-entity-picker">
            <a
              href="#"
              class="small text-muted d-inline-flex align-items-center gap-1 mb-2"
              @click.prevent="step = 'pick-type'">
              <IconArrowLeft :size="14" /> {{ $t('btn.back') }}
            </a>

            <template v-if="selectedMode === 'existing'">
              <div data-testid="user-emails-selector-existing-combobox">
                <LeadCombobox
                  :model-value="pendingSelection"
                  :items="availableUserOptions"
                  display-initials
                  :placeholder="$t('user_emails_selector.users.placeholder')"
                  :loading="loadingUsers"
                  :clearable="clearable"
                  @update:model-value="handleExistingUserSelection" />
              </div>
              <button
                type="button"
                class="btn btn-primary w-100 mt-2"
                data-testid="user-emails-selector-add-existing"
                :disabled="!pendingSelection"
                @click="addSelectedExistingUser">
                {{ $t('btn.add') }}
              </button>
            </template>

            <template v-else>
              <div class="manual-email-input input-group">
                <input
                  v-model="manualEmail"
                  type="email"
                  class="form-control"
                  data-testid="user-emails-selector-manual-input"
                  :placeholder="$t('user_emails_selector.email.placeholder')"
                  @keydown.enter.prevent="addManualEmailFromInput" />
                <button
                  type="button"
                  class="btn btn-outline-secondary"
                  data-testid="user-emails-selector-add-manual"
                  @click="addManualEmailFromInput">
                  {{ $t('btn.add') }}
                </button>
              </div>
            </template>

            <div v-if="localError" class="text-danger mt-2 small">{{ localError }}</div>
          </div>
        </div>
      </div>
    </div>

    <FormErrorList
      v-if="error && (Array.isArray(error) ? error.length > 0 : true)"
      :errors="Array.isArray(error) ? error : [error]" />
  </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { FormErrorList } from '@prospective/lithium';
import { IconArrowLeft, IconMail, IconPlus, IconUser, IconX } from '@tabler/icons-vue';
import LeadCombobox from '@/components/form/LeadCombobox.vue';
import { fetchCustomerAccounts } from '@/api/customer';

export interface ContactOption {
  id?: number;
  name: string;
  email: string;
}

const props = withDefaults(
  defineProps<{
    label?: string;
    required?: boolean;
    clearable?: boolean;
    initialEmails?: string[] | null;
    disabled?: boolean;
    error?: string | string[] | null;
  }>(),
  {
    label: '',
    required: false,
    clearable: true,
    initialEmails: null,
    disabled: false,
    error: null,
  }
);

const modelContacts = defineModel<ContactOption[]>({ default: () => [] });

const emit = defineEmits<{
  (e: 'change', contacts: ContactOption[]): void;
}>();

const userOptions = ref<ContactOption[]>([]);
const loadingUsers = ref(false);
const pendingSelection = ref<ContactOption | null>(null);
const manualEmail = ref('');
const localError = ref<string | null>(null);
const selectedContacts = ref<ContactOption[]>([]);
// Once the user has explicitly added/removed a contact, applyPreselection must never run
// again — otherwise a late-resolving initialEmails update (e.g. the store's order data
// refreshing from an in-flight draft save) can silently re-add a contact the user just
// removed, since applyPreselection's own guard only checks for an empty list.
const userHasInteracted = ref(false);
const popoverRef = ref<HTMLElement | null>(null);
const isPopoverOpen = ref(false);
const step = ref<'pick-type' | 'pick-value'>('pick-type');
const selectedMode = ref<'existing' | 'manual' | null>(null);

const selectedEmailSet = computed(() => new Set(selectedContacts.value.map((c) => c.email.toLowerCase())));

const availableUserOptions = computed(() => {
  return userOptions.value.filter((user) => !selectedEmailSet.value.has(user.email.toLowerCase()));
});

function emitChange() {
  emit('change', selectedContacts.value);
}

function setSelectedContacts(nextContacts: ContactOption[]) {
  selectedContacts.value = nextContacts;
  modelContacts.value = nextContacts;
  emitChange();
}

function isValidEmail(value: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function addContact(contact: ContactOption) {
  userHasInteracted.value = true;
  const normalizedEmail = contact.email.trim().toLowerCase();
  if (!normalizedEmail) return;

  const existingIndex = selectedContacts.value.findIndex((c) => c.email.toLowerCase() === normalizedEmail);
  if (existingIndex !== -1) {
    // Replace manual entries with known users when available.
    const nextContacts = [...selectedContacts.value];
    nextContacts.splice(existingIndex, 1, contact);
    setSelectedContacts(nextContacts);
    return;
  }

  setSelectedContacts([...selectedContacts.value, contact]);
}

function handleClickOutside(event: MouseEvent) {
  if (popoverRef.value && !popoverRef.value.contains(event.target as Node)) {
    closePopover();
  }
}

function closePopover() {
  isPopoverOpen.value = false;
  step.value = 'pick-type';
  selectedMode.value = null;
  pendingSelection.value = null;
  manualEmail.value = '';
  localError.value = null;
  document.removeEventListener('mousedown', handleClickOutside);
}

function togglePopover() {
  if (isPopoverOpen.value) {
    closePopover();
    return;
  }

  isPopoverOpen.value = true;
  step.value = 'pick-type';
  selectedMode.value = null;
  localError.value = null;
  document.addEventListener('mousedown', handleClickOutside);
}

function selectMode(mode: 'existing' | 'manual') {
  selectedMode.value = mode;
  step.value = 'pick-value';
  localError.value = null;
}

function removeContact(email: string) {
  userHasInteracted.value = true;
  const normalizedEmail = email.toLowerCase();
  setSelectedContacts(selectedContacts.value.filter((c) => c.email.toLowerCase() !== normalizedEmail));
}

function addManualEmailFromInput() {
  localError.value = null;
  const email = manualEmail.value.trim();

  if (!email) return;
  if (!isValidEmail(email)) {
    localError.value = 'Please enter a valid email address.';
    return;
  }

  const existingUser = userOptions.value.find((u) => u.email.toLowerCase() === email.toLowerCase());
  if (existingUser) {
    addContact(existingUser);
  } else {
    addContact({
      name: email,
      email,
    });
  }

  manualEmail.value = '';
  closePopover();
}

function handleExistingUserSelection(value: ContactOption | string | null) {
  if (!value || typeof value === 'string') return;
  localError.value = null;
  pendingSelection.value = value;
}

function addSelectedExistingUser() {
  if (!pendingSelection.value) return;
  addContact(pendingSelection.value);
  closePopover();
}

function applyPreselection() {
  if (userHasInteracted.value) return;
  if (selectedContacts.value.length > 0) return;

  const contacts: ContactOption[] = [];
  const usedEmails = new Set<string>();

  for (const email of props.initialEmails ?? []) {
    const cleanedEmail = email.trim();
    if (!cleanedEmail) continue;

    const key = cleanedEmail.toLowerCase();
    if (usedEmails.has(key)) continue;

    const match = userOptions.value.find((u) => u.email.toLowerCase() === key);
    if (match) {
      usedEmails.add(key);
      contacts.push(match);
      continue;
    }

    usedEmails.add(key);
    contacts.push({
      name: cleanedEmail,
      email: cleanedEmail,
    });
  }

  if (contacts.length > 0) {
    setSelectedContacts(contacts);
  }
}

async function loadUsers() {
  loadingUsers.value = true;
  try {
    const response = await fetchCustomerAccounts();
    userOptions.value = response.data.map((user: any) => {
      const { firstname, lastname, email } = user;
      const name = firstname || lastname ? `${firstname ?? ''} ${lastname ?? ''}`.trim() : email;
      return {
        id: user.id,
        name,
        email,
      };
    });
    applyPreselection();
  } catch (e) {
    console.error("Couldn't get the users", e);
  } finally {
    loadingUsers.value = false;
  }
}

watch(
  () => props.initialEmails,
  () => {
    applyPreselection();
  }
);

watch(
  modelContacts,
  (newValue) => {
    selectedContacts.value = Array.isArray(newValue) ? [...newValue] : [];
  },
  { immediate: true }
);

onMounted(loadUsers);

onBeforeUnmount(() => {
  document.removeEventListener('mousedown', handleClickOutside);
});
</script>

<style scoped lang="scss">
.selected-emails {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.email-chip {
  max-width: 100%;
}

.chip-label {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chip-remove {
  flex: 0 0 auto;
}

.selector-popover {
  position: relative;
  display: inline-block;
}

.selector-popover-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1000;
  margin-top: 4px;
  border-radius: 6px;
}

.selector-popover-item {
  color: inherit;
  text-decoration: none;
  border-radius: 4px;
  margin: 0 4px;
  transition: background-color 0.15s;
}

.selector-popover-item:hover {
  background-color: #f1f5f9;
}

.selector-popover-trigger {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
}

.selector-popover-type-list {
  width: 260px;
}

.selector-popover-entity-picker {
  width: 340px;
}
</style>
